import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { Header } from "@/components/sections/Header";
import { TrustBar } from "@/components/sections/TrustBar";
import { GoogleReviews } from "@/components/sections/GoogleReviews";
import { ServicesInclude } from "@/components/sections/ServicesInclude";
import { Industries } from "@/components/sections/Industries";
import { PricingCalculator } from "@/components/sections/PricingCalculator";
import { Gallery } from "@/components/sections/Gallery";
import { Testimonials } from "@/components/sections/Testimonials";
import { Faq } from "@/components/sections/Faq";
import { ContactForm } from "@/components/sections/ContactForm";
import { Footer } from "@/components/sections/Footer";
import { StickyCallBar } from "@/components/sections/StickyCallBar";
import { OfferModal } from "@/components/sections/OfferModal";
import { CONTACT } from "@/data/content";
import { getCity, CITIES } from "@/data/cities";

const useSeo = (city) => {
  useEffect(() => {
    if (!city) return;
    const title = `Commercial Cleaning in ${city.name}, FL | Elite Facility Solutions`;
    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [a, v] = selector.replace(/[[\]']/g, "").split("=");
        el.setAttribute(a.includes("name") ? "name" : "property", v);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta("meta[name='description']", "content", city.metaDescription);
    setMeta("meta[property='og:title']", "content", title);
    setMeta("meta[property='og:description']", "content", city.metaDescription);

    const schema = {
      "@context": "https://schema.org",
      "@type": "CleaningService",
      name: `Elite Facility Solutions — ${city.name}`,
      description: city.metaDescription,
      telephone: "+1-786-918-1367",
      email: CONTACT.email,
      url: `https://${CONTACT.domain}/commercial-cleaning/${city.slug}`,
      areaServed: { "@type": "City", name: `${city.name}, FL` },
      address: { "@type": "PostalAddress", addressLocality: city.name, addressRegion: "FL", addressCountry: "US" },
      priceRange: "$$",
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = "city-schema";
    tag.text = JSON.stringify(schema);
    document.getElementById("city-schema")?.remove();
    document.head.appendChild(tag);

    return () => {
      document.title = "Elite Facility Solutions | Commercial Cleaning & Janitorial Services";
      document.getElementById("city-schema")?.remove();
    };
  }, [city]);
};

export default function CityLanding() {
  const { slug } = useParams();
  const city = getCity(slug);
  useSeo(city);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!city) return <Navigate to="/" replace />;

  const nearbyCities = city.nearby.map(getCity).filter(Boolean);

  return (
    <div data-testid={`city-page-${city.slug}`} className="bg-white">
      <Header />
      <main>
        {/* City Hero */}
        <section className="relative min-h-[80vh] flex items-center pt-20 overflow-hidden" data-testid="city-hero">
          <div className="absolute inset-0">
            <img src={city.heroImage} alt={`Commercial cleaning in ${city.name}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#1c4f9c]/85" />
            <div className="absolute inset-0 grain-overlay opacity-[0.15] mix-blend-overlay" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
            <nav className="flex items-center gap-2 text-sm text-blue-200 mb-6" data-testid="breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white font-medium">{city.name}</span>
            </nav>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#ff6b70] mb-4">{city.region} · Florida</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-4xl">
              Commercial Cleaning Services in {city.name}, FL
            </h1>
            <p className="mt-6 text-lg text-blue-100/90 leading-relaxed max-w-2xl">{city.intro}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href="#contact" data-testid="city-estimate-btn" className="inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-4 px-8 rounded-sm hover:bg-[#c81e24] transition-colors duration-200">
                Request a Free Estimate <ArrowRight size={18} strokeWidth={2.5} />
              </a>
              <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white font-bold py-4 px-8 rounded-sm hover:bg-white hover:text-[#1c4f9c] transition-colors duration-200">
                <Phone size={18} strokeWidth={2.5} /> Call {CONTACT.phone}
              </a>
            </div>
          </div>
        </section>

        <TrustBar />
        <GoogleReviews />

        {/* Localized intro + neighborhoods */}
        <section className="py-24 sm:py-32 bg-white" data-testid="city-content">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e02329] mb-3">Your Local Cleaning Partner</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1c4f9c]">
                Trusted commercial cleaning across {city.name}
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">{city.blurb}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                {["Daily, weekly & custom schedules", "Fully insured & bonded teams", "Eco-friendly cleaning products", "Free on-site walkthroughs"].map((p) => (
                  <li key={p} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-[#e02329] shrink-0 mt-0.5" />
                    <span className="text-sm font-medium text-slate-700">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#f1f5f9] border border-slate-200 rounded-sm p-8">
              <h3 className="text-xl font-bold text-[#1c4f9c] flex items-center gap-2">
                <MapPin size={20} className="text-[#e02329]" /> Areas We Serve in {city.name}
              </h3>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {city.neighborhoods.map((n) => (
                  <span key={n} className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 px-3 py-2 rounded-sm">
                    <span className="w-1.5 h-1.5 bg-[#e02329] rounded-full" /> {n}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <ServicesInclude />
        <Industries />
        <PricingCalculator />
        <Gallery />
        <Testimonials />

        {/* Nearby cities internal links */}
        <section className="py-20 bg-[#f1f5f9]" data-testid="nearby-cities">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1c4f9c] mb-8">We also serve nearby Florida communities</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {nearbyCities.map((nc) => (
                <Link
                  key={nc.slug}
                  to={`/commercial-cleaning/${nc.slug}`}
                  data-testid={`nearby-link-${nc.slug}`}
                  className="group flex items-center justify-between border-2 border-slate-200 bg-white p-6 rounded-sm hover:border-[#e02329] transition-colors duration-200"
                >
                  <span>
                    <span className="block text-lg font-bold text-[#1c4f9c]">{nc.name}</span>
                    <span className="block text-xs text-slate-500">{nc.region}</span>
                  </span>
                  <ArrowRight size={20} className="text-[#e02329] group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Faq />
        <ContactForm />
      </main>
      <Footer />
      <StickyCallBar />
      <OfferModal />
    </div>
  );
}

export { CITIES };
